import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Store, Handshake, Megaphone, Wrench, Mail, Globe2, Facebook,
  CheckCircle2, Send, Loader2, ListPlus,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const HELP = [
  {
    icon: Store,
    title: "Business Listings",
    x: "Own a Burmese business? We'd love to help people discover it! Submit your business to our directory and become part of our growing network.",
    note: "Already have a listing? Contact us to update your information, correct details, or request removal.",
    link: { to: "/submit", label: "Submit Your Business", testid: "contact-help-submit" },
  },
  {
    icon: Handshake,
    title: "Partnerships & Collaborations",
    x: "Interested in collaborating with BurmeseHub? We welcome opportunities to connect with businesses, organizations, community groups, and professionals who share our mission of supporting Burmese communities across America.",
  },
  {
    icon: Megaphone,
    title: "Advertising & Business Promotion",
    x: "Looking for opportunities to introduce your business to more people? Reach out to discuss potential advertising, sponsorships, and promotional collaborations.",
    link: { to: "/advertise", label: "Explore Advertising", testid: "contact-help-advertise" },
  },
  {
    icon: Wrench,
    title: "Website Support & Feedback",
    x: "Found incorrect information, experienced a technical issue, or have an idea for improving BurmeseHub? Your feedback matters. We're always looking for ways to make our platform more useful and accessible.",
  },
];

const SUBJECTS = [
  "General Inquiry",
  "Business Listing Support",
  "Update or Remove a Listing",
  "Advertising & Partnerships",
  "Technical Support",
  "Privacy or Legal Request",
  "Other",
];

const EMPTY = { name: "", email: "", business_name: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [socials, setSocials] = useState({});

  useEffect(() => {
    document.title = "Contact Us — BurmeseHub";
    window.scrollTo(0, 0);
    api.get("/settings/public").then((r) => setSocials(r.data || {})).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-sand/60 text-sm outline-none border border-transparent focus:border-gold";

  return (
    <div className="bg-cream min-h-screen" data-testid="contact-page">
      {/* Header band */}
      <section className="bg-navy acheik-bg-dark text-white py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-gold mb-4">
            <Mail className="w-4 h-4" /> Contact Us
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="contact-title">
            We'd Love to Hear From You!
          </h1>
          <p className="mt-5 text-white/70 text-sm sm:text-base leading-relaxed">
            At BurmeseHub, we're building more than just a business directory. We're creating a place where Burmese businesses, professionals, and communities across the United States can discover opportunities, build connections, and grow together.
          </p>
          <p className="mt-3 text-white/70 text-sm sm:text-base leading-relaxed">
            Whether you're a business owner looking to reach more customers, a community member with a suggestion, or someone interested in working with us, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* How can we help */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-navy tracking-tight text-center mb-10" data-testid="contact-help-title">
            How Can We Help?
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {HELP.map((h, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-7 shadow-ambient" data-testid={`contact-help-card-${i}`}>
                <span className="w-11 h-11 rounded-full bg-gold-light border border-gold/40 flex items-center justify-center text-gold-dark mb-4">
                  <h.icon className="w-5 h-5" />
                </span>
                <h3 className="font-sans font-bold text-navy mb-2">{h.title}</h3>
                <p className="text-sm text-clay leading-relaxed">{h.x}</p>
                {h.note && <p className="text-xs text-stone2 leading-relaxed mt-3">{h.note}</p>}
                {h.link && (
                  <Link to={h.link.to} data-testid={h.link.testid}
                    className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-navy-royal hover:text-gold-dark transition-colors">
                    {h.link.label} →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + other ways */}
      <section className="pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-9 shadow-ambient" data-testid="contact-form-card">
              {sent ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <CheckCircle2 className="w-14 h-14 text-jade mx-auto mb-6" />
                  <h2 className="font-serif text-2xl font-bold text-navy mb-4">Message Sent</h2>
                  <p className="text-clay leading-relaxed max-w-md mx-auto">
                    Thank you for contacting BurmeseHub! Your message has been received successfully. We appreciate you reaching out and will do our best to respond as soon as possible.
                  </p>
                  <button
                    onClick={() => { setForm(EMPTY); setSent(false); }}
                    className="mt-8 border border-[#E6DED3] text-ink text-sm font-semibold px-6 py-3 rounded-full hover:border-gold transition-colors"
                    data-testid="contact-send-another-btn"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-serif text-2xl sm:text-3xl font-bold text-navy tracking-tight mb-2" data-testid="contact-form-title">
                    Send Us a Message
                  </h2>
                  <p className="text-sm text-clay mb-7">
                    Have a question or inquiry? Complete our contact form, and our team will review your message.
                  </p>
                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-navy uppercase tracking-wider mb-2">Full Name (Required)</label>
                        <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-navy uppercase tracking-wider mb-2">Email Address (Required)</label>
                        <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-navy uppercase tracking-wider mb-2">Business Name (Optional)</label>
                        <input value={form.business_name} onChange={set("business_name")} className={inputCls} data-testid="contact-business-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-navy uppercase tracking-wider mb-2">Subject (Required)</label>
                        <select required value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-select">
                          <option value="" disabled>Select a subject</option>
                          {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-navy uppercase tracking-wider mb-2">Message (Required)</label>
                      <textarea required rows={6} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} data-testid="contact-message-input" />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark disabled:opacity-60 text-navy text-sm font-bold px-7 py-3.5 rounded-full transition-colors"
                      data-testid="contact-submit-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send Message
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="bg-navy acheik-bg-dark text-white rounded-2xl p-6 sm:p-8" data-testid="contact-other-ways">
              <h3 className="font-serif text-xl font-bold mb-6">Other Ways to Connect</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Globe2 className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-white/90">Website</p>
                    <Link to="/" className="text-white/60 hover:text-gold transition-colors" data-testid="contact-website-link">https://www.burmesehub.com</Link>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-white/90">Email</p>
                    <a href="mailto:admin@burmesehub.com" className="text-white/60 hover:text-gold transition-colors" data-testid="contact-email-link">admin@burmesehub.com</a>
                  </div>
                </li>
                {socials.facebook_url && (
                  <li className="flex items-start gap-3">
                    <Facebook className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-white/90">Facebook</p>
                      <a href={socials.facebook_url} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-gold transition-colors" data-testid="contact-facebook-link">
                        BurmeseHub.com Official Facebook Page
                      </a>
                    </div>
                  </li>
                )}
                <li className="flex items-start gap-3">
                  <ListPlus className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-white/90">Business Listing Submission</p>
                    <Link to="/submit" className="text-white/60 hover:text-gold transition-colors" data-testid="contact-submit-link">https://burmesehub.com/submit</Link>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 shadow-ambient" data-testid="contact-closing-card">
              <h3 className="font-serif text-xl font-bold text-navy mb-3">Let's Grow Together!</h3>
              <p className="text-sm text-clay leading-relaxed">
                We believe stronger communities begin with meaningful connections. Every business we help people discover and every connection we help create brings us closer to that goal.
              </p>
              <p className="text-sm text-clay leading-relaxed mt-3">
                Thank you for being part of the BurmeseHub community!
              </p>
              <p className="mt-6 font-serif italic text-lg text-gold-dark">BurmeseHub.com — Find. Connect. Belong.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
